import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Gift } from "lucide-react";
import { GoogleButton } from "@/components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password, ref);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 bg-[#F9F8F6] order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <h1 className="font-display font-bold text-3xl text-[#0A2E26]">Become a member</h1>
          <p className="text-[#5C5C5C] mt-2">Create your account, then invest $100 per share.</p>

          {ref && (
            <div data-testid="referral-banner" className="mt-4 flex items-center gap-2 text-sm text-[#0A2E26] bg-[#E7F6EF] border border-[#059669]/20 rounded-md px-3 py-2">
              <Gift className="w-4 h-4 text-[#059669]" /> You were invited by a member — welcome!
            </div>
          )}

          {error && (
            <div data-testid="register-error" className="mt-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name" value={name}
                onChange={(e) => setName(e.target.value)} required className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5 bg-white" />
            </div>
          </div>

          <Button type="submit" data-testid="register-submit" disabled={loading}
            className="w-full mt-6 bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md h-11">
            {loading ? "Creating account…" : "Create account"}
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-[#E5E5E5]" />
            <span className="text-xs text-[#5C5C5C]">or</span>
            <div className="h-px flex-1 bg-[#E5E5E5]" />
          </div>
          <GoogleButton label="Sign up with Google" />

          <p className="text-sm text-[#5C5C5C] mt-5 text-center">
            Already a member?{" "}
            <Link to="/login" data-testid="to-login" className="text-[#0A2E26] font-semibold hover:underline">
              Log in
            </Link>
          </p>
        </form>
      </div>

      <div className="hidden lg:block relative bg-[#0A2E26] noise-overlay order-1 lg:order-2">
        <img
          src="https://images.unsplash.com/photo-1541205646242-30258c7485b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Neighborhood"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative p-12 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 justify-end">
            <span className="font-display font-bold text-lg text-white">EquiBrick</span>
            <div className="w-8 h-8 rounded-md bg-[#C49A45] flex items-center justify-center">
              <Building2 className="w-4 h-4 text-[#0A2E26]" />
            </div>
          </Link>
          <div className="text-right">
            <h2 className="font-display font-bold text-3xl text-white leading-snug">
              2,000 members.<br />One shared portfolio.
            </h2>
            <p className="text-white/60 mt-3">Buy, rehab, sell — repeat.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
